import { apiClient } from './apiClient';
import type {
  ProducerContact,
  ContactFormData,
  ContactUpdateData,
  ContactFullUpdateData,
  PaginatedContactResponse,
  PaginatedDuplicateResponse,
  MergeContactsRequest,
  MergeContactsResponse,
  UnifiedContact,
} from '../../types/contact';

/**
 * Contact API Service
 * Handles all contact-related API operations for producer contact management
 */
export class ContactService {
  /**
   * Get all contacts for a specific producer
   * 
   * @param producerId - The ID of the producer
   * @param page - Optional page number for pagination
   * @returns Promise with paginated list of producer contacts
   * 
   * @example
   * ```typescript
   * const response = await contactService.getProducerContacts(123);
   * console.log(response.results); // Array of ProducerContact
   * ```
   */
  async getProducerContacts(
    producerId: number,
    page?: number
  ): Promise<PaginatedContactResponse> {
    try {
      const params = page ? { page } : {};
      const response = await apiClient.get<PaginatedContactResponse>(
        `/producers/${producerId}/contacts/`,
        { params }
      );
      
      return response.data;
    } catch (error: any) {
      throw {
        message: error.message || 'Error al cargar contactos del productor',
        status: error.status,
        originalError: error,
      };
    }
  }

  /**
   * Get contacts for the producer associated with an audit
   * 
   * @param auditId - The ID of the audit
   * @param page - Optional page number for pagination
   * @returns Promise with paginated list of producer contacts
   * 
   * @example
   * ```typescript
   * const response = await contactService.getAuditProducerContacts(456);
   * console.log(response.results); // Array of ProducerContact
   * ```
   */
  async getAuditProducerContacts(
    auditId: number,
    page?: number
  ): Promise<PaginatedContactResponse> {
    try {
      const params = page ? { page } : {};
      const response = await apiClient.get<PaginatedContactResponse>(
        `/audits/${auditId}/producer/contacts/`,
        { params }
      );
      
      return response.data;
    } catch (error: any) {
      throw {
        message: error.message || 'Error al cargar contactos del productor de la auditoría',
        status: error.status,
        originalError: error,
      };
    }
  }

  /**
   * Create a new contact for a producer
   * If a contact with the same email/phone already exists, it will be reused
   * 
   * @param producerId - The ID of the producer
   * @param data - Contact form data
   * @returns Promise with the created producer contact
   * 
   * @example
   * ```typescript
   * const newContact = await contactService.createProducerContact(123, {
   *   name: 'Juan',
   *   surname: 'Pérez',
   *   email: 'juan@example.com',
   *   main_phone: '+34600000000',
   *   position_id: 1,
   *   is_favorite: true
   * });
   * ```
   */
  async createProducerContact(
    producerId: number,
    data: ContactFormData
  ): Promise<ProducerContact> {
    try {
      const response = await apiClient.post<ProducerContact>(
        `/producers/${producerId}/contacts/`,
        data
      );
      
      return response.data;
    } catch (error: any) {
      // Handle validation errors from backend
      if (error.originalError?.response?.data) {
        throw {
          message: 'Error de validación al crear contacto',
          validationErrors: error.originalError.response.data,
          status: error.status,
          originalError: error,
        };
      }
      
      throw {
        message: error.message || 'Error al crear contacto',
        status: error.status,
        originalError: error,
      };
    }
  }

  /**
   * Update a producer-contact relationship
   * Only updates relationship fields (position, is_favorite, is_main_contact, notes)
   * Does not update personal contact data (name, email, phone)
   * 
   * @param producerContactId - The ID of the producer-contact relationship
   * @param data - Fields to update
   * @returns Promise with the updated producer contact
   * 
   * @example
   * ```typescript
   * const updated = await contactService.updateProducerContact('uuid', {
   *   position_id: 2,
   *   is_favorite: true,
   *   notes: 'Updated notes'
   * });
   * ```
   */
  async updateProducerContact(
    producerContactId: string,
    data: ContactUpdateData
  ): Promise<ProducerContact> {
    try {
      const response = await apiClient.patch<ProducerContact>(
        `/producer-contacts/${producerContactId}/`,
        data
      );
      
      return response.data;
    } catch (error: any) {
      // Handle validation errors from backend
      if (error.originalError?.response?.data) {
        throw {
          message: 'Error de validación al actualizar contacto',
          validationErrors: error.originalError.response.data,
          status: error.status,
          originalError: error,
        };
      }
      
      throw {
        message: error.message || 'Error al actualizar contacto',
        status: error.status,
        originalError: error,
      };
    }
  }

  /**
   * Update full producer contact (including personal data)
   * Updates both the UnifiedContact data and the ProducerContact relationship
   * 
   * @param producerContactId - The ID of the producer-contact relationship
   * @param contactId - The ID of the unified contact
   * @param data - Full contact data to update
   * @returns Promise with the updated producer contact
   * 
   * @example
   * ```typescript
   * const updated = await contactService.updateFullProducerContact('uuid', 'contact-uuid', {
   *   name: 'Juan',
   *   surname: 'Pérez',
   *   email: 'juan@example.com',
   *   main_phone: '+34 600000000',
   *   position_id: 2,
   *   is_favorite: true
   * });
   * ```
   */
  async updateFullProducerContact(
    producerContactId: string,
    contactId: string,
    data: ContactFullUpdateData
  ): Promise<ProducerContact> {
    try {
      // Separate contact data from relationship data
      const { name, surname, email, main_phone, secondary_phone, identification_number, ...relationshipData } = data;
      
      // Update UnifiedContact data if any personal fields are provided
      if (name || surname || email || main_phone || secondary_phone || identification_number) {
        const contactData: any = {};
        if (name !== undefined) contactData.name = name;
        if (surname !== undefined) contactData.surname = surname;
        if (email !== undefined) contactData.email = email || null;
        if (main_phone !== undefined) contactData.main_phone = main_phone || null;
        if (secondary_phone !== undefined) contactData.secondary_phone = secondary_phone || null;
        if (identification_number !== undefined) contactData.identification_number = identification_number || null;
        
        await apiClient.patch(`/admin/contacts/${contactId}/`, contactData);
      }
      
      // Update ProducerContact relationship data if any relationship fields are provided
      if (Object.keys(relationshipData).length > 0) {
        const response = await apiClient.patch<ProducerContact>(
          `/producer-contacts/${producerContactId}/`,
          relationshipData
        );
        return response.data;
      }
      
      // If only contact data was updated, fetch the updated producer contact
      const response = await apiClient.get<ProducerContact>(
        `/producer-contacts/${producerContactId}/`
      );
      return response.data;
      
    } catch (error: any) {
      // Handle validation errors from backend
      if (error.originalError?.response?.data) {
        throw {
          message: 'Error de validación al actualizar contacto',
          validationErrors: error.originalError.response.data,
          status: error.status,
          originalError: error,
        };
      }
      
      throw {
        message: error.message || 'Error al actualizar contacto',
        status: error.status,
        originalError: error,
      };
    }
  }

  /**
   * Delete a producer-contact relationship (soft delete)
   * Marks the relationship as inactive instead of deleting it
   * 
   * @param producerContactId - The ID of the producer-contact relationship
   * @returns Promise that resolves when deletion is complete
   * 
   * @example
   * ```typescript
   * await contactService.deleteProducerContact('uuid');
   * ```
   */
  async deleteProducerContact(producerContactId: string): Promise<void> {
    try {
      await apiClient.delete(`/producer-contacts/${producerContactId}/`);
    } catch (error: any) {
      throw {
        message: error.message || 'Error al eliminar contacto',
        status: error.status,
        originalError: error,
      };
    }
  }

  /**
   * Toggle favorite status of a producer contact
   * 
   * @param producerContactId - The ID of the producer-contact relationship
   * @returns Promise with the updated producer contact
   * 
   * @example
   * ```typescript
   * const updated = await contactService.toggleFavorite('uuid');
   * console.log(updated.is_favorite); // true or false
   * ```
   */
  async toggleFavorite(producerContactId: string): Promise<ProducerContact> {
    try {
      const response = await apiClient.patch<ProducerContact>(
        `/producer-contacts/${producerContactId}/toggle-favorite/`
      );
      
      return response.data;
    } catch (error: any) {
      throw {
        message: error.message || 'Error al cambiar estado de favorito',
        status: error.status,
        originalError: error,
      };
    }
  }

  /**
   * Get duplicate contact groups (Admin only)
   * Returns groups of contacts that have matching email, phone, or ID number
   * 
   * @param page - Optional page number for pagination
   * @returns Promise with paginated list of duplicate groups
   * 
   * @example
   * ```typescript
   * const duplicates = await contactService.getDuplicates();
   * duplicates.results.forEach(group => {
   *   console.log(`Found ${group.contacts.length} duplicates for ${group.match_field}`);
   * });
   * ```
   */
  async getDuplicates(page?: number): Promise<PaginatedDuplicateResponse> {
    try {
      const params = page ? { page } : {};
      const response = await apiClient.get<PaginatedDuplicateResponse>(
        '/admin/contacts/duplicates/',
        { params }
      );
      
      return response.data;
    } catch (error: any) {
      throw {
        message: error.message || 'Error al cargar contactos duplicados',
        status: error.status,
        originalError: error,
      };
    }
  }

  /**
   * Merge duplicate contacts into a single primary contact (Admin only)
   * All relationships from duplicate contacts will be migrated to the primary contact
   * 
   * @param request - Merge request with primary contact ID and duplicate IDs
   * @returns Promise with merge operation results
   * 
   * @example
   * ```typescript
   * const result = await contactService.mergeContacts({
   *   primary_contact_id: 'uuid1',
   *   duplicate_contact_ids: ['uuid2', 'uuid3']
   * });
   * console.log(`Merged ${result.contacts_merged} contacts`);
   * console.log(`Migrated ${result.relations_migrated} relationships`);
   * ```
   */
  async mergeContacts(request: MergeContactsRequest): Promise<MergeContactsResponse> {
    try {
      const response = await apiClient.post<MergeContactsResponse>(
        '/admin/contacts/merge/',
        request
      );
      
      return response.data;
    } catch (error: any) {
      // Handle validation errors from backend
      if (error.originalError?.response?.data) {
        throw {
          message: error.originalError.response.data.message || 'Error al fusionar contactos',
          validationErrors: error.originalError.response.data,
          status: error.status,
          originalError: error,
        };
      }
      
      throw {
        message: error.message || 'Error al fusionar contactos',
        status: error.status,
        originalError: error,
      };
    }
  }

  /**
   * Update personal contact data (Admin only)
   * Updates the UnifiedContact data (name, email, phone, etc.)
   * 
   * @param contactId - The ID of the unified contact
   * @param data - Partial contact data to update
   * @returns Promise with the updated contact
   * 
   * @example
   * ```typescript
   * const updated = await contactService.updateContactData('uuid', {
   *   email: 'newemail@example.com',
   *   main_phone: '+34600111111'
   * });
   * ```
   */
  async updateContactData(
    contactId: string,
    data: Partial<Omit<UnifiedContact, 'id' | 'created' | 'modified'>>
  ): Promise<UnifiedContact> {
    try {
      const response = await apiClient.patch<UnifiedContact>(
        `/admin/contacts/${contactId}/`,
        data
      );
      
      return response.data;
    } catch (error: any) {
      // Handle validation errors from backend
      if (error.originalError?.response?.data) {
        throw {
          message: 'Error de validación al actualizar datos del contacto',
          validationErrors: error.originalError.response.data,
          status: error.status,
          originalError: error,
        };
      }
      
      throw {
        message: error.message || 'Error al actualizar datos del contacto',
        status: error.status,
        originalError: error,
      };
    }
  }
}

/**
 * Singleton instance of ContactService
 */
export const contactService = new ContactService();

export default contactService;
